import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Settings, Users, School, Database, BarChart3, Bell, Calendar } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import { useAuth } from '../../contexts/AuthContext';
import { useUserRole } from '../../hooks/useUserRole';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

export default function SettingsPage() {
  useDocumentTitle('Settings');
  
  const { user } = useAuth();
  const { isAdmin, loading } = useUserRole();
  const navigate = useNavigate();

  const settingsGroups = [
    {
      title: 'Personal Settings',
      items: [
        {
          icon: <Settings className="w-5 h-5" />,
          title: 'Account Settings',
          description: 'Update your profile, email, and password',
          link: '/profile'
        },
        {
          icon: <Bell className="w-5 h-5" />,
          title: 'Notification Preferences',
          description: 'Manage how you receive notifications',
          link: '/settings/notifications'
        },
        {
          icon: <Calendar className="w-5 h-5" />,
          title: 'Calendar Settings',
          description: 'Configure calendar integrations and sync',
          link: '/settings/calendar'
        }
      ]
    },
    {
      title: 'School Administration',
      items: [
        {
          icon: <Users className="w-5 h-5" />,
          title: 'User Management',
          description: 'Manage staff accounts and permissions',
          link: '/settings/admin',
          adminOnly: true
        },
        {
          icon: <School className="w-5 h-5" />,
          title: 'School Settings',
          description: 'Configure school details and academic calendar',
          link: '/settings/school',
          adminOnly: true
        }
      ]
    },
    {
      title: 'Integrations',
      items: [
        {
          icon: <Database className="w-5 h-5" />,
          title: 'LMS Integration',
          description: 'Configure and manage LMS connections',
          link: '/settings/lms',
          adminOnly: true
        }
      ]
    },
    {
      title: 'Analytics & Reports',
      items: [
        {
          icon: <BarChart3 className="w-5 h-5" />,
          title: 'Usage Analytics',
          description: 'View system usage and engagement metrics',
          link: '/settings/analytics',
          adminOnly: true
        }
      ]
    }
  ];

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-full">
          <div className="text-text-secondary">Loading...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-semibold text-text-primary mb-6">Settings</h1>

          <div className="space-y-8">
            {settingsGroups.map((group) => {
              // Filter out admin-only items if user is not an admin
              const filteredItems = group.items.filter(item => !item.adminOnly || isAdmin);
              
              // Don't render the group if no items are available
              if (filteredItems.length === 0) return null;
              
              return (
                <div key={group.title}>
                  <h2 className="text-lg font-semibold text-text-primary mb-4">{group.title}</h2>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {filteredItems.map((item) => (
                      <Link
                        key={item.title}
                        to={item.link}
                        className="p-4 bg-surface-light rounded-lg hover:shadow-md transition-shadow"
                      >
                        <div className="flex items-start gap-4">
                          <div className="p-2 bg-primary-50 rounded-lg">
                            {item.icon}
                          </div>
                          <div>
                            <h3 className="font-medium text-text-primary">{item.title}</h3>
                            <p className="text-sm text-text-secondary">{item.description}</p>
                          </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}